import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteConfigProvider, useSiteConfig } from "@/context/SiteConfigContext";
import Landing from "@/pages/Landing";
import VersionWatcher from "@/components/VersionWatcher";
import InstallPrompt from "@/components/InstallPrompt";
import PinGate, { pinAlreadyPassed, PIN_OK_KEY } from "@/components/PinGate";
import { lazy, Suspense, useEffect, useState } from "react";

// Code-split everything that isn't needed to paint the landing page: the
// signed-in dashboard (maps, chat, admin, invoices…) and the public side pages
// each load as their own chunk on demand.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PublicTrack = lazy(() => import("@/pages/PublicTrack"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const Promo = lazy(() => import("@/pages/Promo"));
const EmbedWidget = lazy(() => import("@/pages/EmbedWidget"));
const PublicPage = lazy(() => import("@/pages/PublicPage"));
const PrivacyPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.TermsOfService })));

function Spinner() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-force-bg" data-testid="route-loading">
            <div className="h-10 w-10 rounded-full border-2 border-orange-400/30 border-t-orange-400 animate-spin" />
        </div>
    );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Android/PWA back button should NEVER exit the app — it should step back
// through history. Push a root sentinel and re-push it if the user backs past
// it, so the webview always stays open. Overlays/views push their own states
// on top (useBackClose / Dashboard view history), so back unwinds those first.
function useAppRootHistory() {
    useEffect(() => {
        window.history.pushState({ __forceRoot: true }, "");
        const onPop = () => {
            const s = window.history.state;
            const isAppState = s && (s.__forceRoot || s.__forceView || s.__forceOverlay);
            if (!isAppState) {
                window.history.pushState({ __forceRoot: true }, "");
            }
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);
}

function Gate() {
    const { user, ready } = useAuth();
    useAppRootHistory();
    const { pinEnabled, ready: cfgReady } = useSiteConfig();
    const [passed, setPassed] = useState(() => pinAlreadyPassed());
    // Bumped on every successful unlock so the Gate ALWAYS re-renders, even when
    // `passed` was already true (Force-PIN with a still-valid 24h pass) — otherwise
    // React bails out of the identical setState and the gate stays stuck on screen.
    const [unlockTick, setUnlockTick] = useState(0);
    // Server-side "force PIN" epoch: any device pass older than this is void.
    const [epochMs, setEpochMs] = useState(null); // null = still loading

    useEffect(() => {
        if (!cfgReady || !pinEnabled) return;
        fetch(`${API}/security/state`)
            .then((r) => r.json())
            .then((d) => setEpochMs(d?.pin_epoch ? Date.parse(d.pin_epoch) : 0))
            .catch(() => setEpochMs(0));
    }, [cfgReady, pinEnabled]);

    if (!ready || !cfgReady || (pinEnabled && epochMs === null)) {
        return <Spinner />;
    }
    // PIN gate sits in front of the app (skipped once authenticated, when disabled
    // by admin, or after a device cleared it in 24h — unless an admin forced
    // re-entry, which applies to EVERYONE including logged-in users).
    // `unlockTick` is read here so this recomputes on every unlock.
    void unlockTick;
    const passTs = Number(localStorage.getItem(PIN_OK_KEY) || 0);
    const passValid = passed && passTs > (epochMs || 0);
    const forced = (epochMs || 0) > 0 && passTs <= epochMs;
    if (pinEnabled && !passValid && (!user || forced)) {
        return <PinGate onUnlock={() => { setPassed(true); setUnlockTick((t) => t + 1); }} />;
    }
    return user ? <Dashboard /> : <Landing />;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <SiteConfigProvider>
                    <BrowserRouter>
                        <VersionWatcher />
                        <InstallPrompt />
                        <Suspense fallback={<Spinner />}>
                        <Routes>
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsOfService />} />
                            {/* Public "watch my pro arrive" link — no login, no PIN */}
                            <Route path="/track/:token" element={<PublicTrack />} />
                            {/* Public pricing + Stripe return pages — no login, no PIN */}
                            <Route path="/promo" element={<Promo />} />
                            <Route path="/pricing" element={<PricingPage />} />
                            {/* Public embeddable job board snippet — no login, no PIN */}
                            <Route path="/embed" element={<EmbedWidget />} />
                            {/* Public pro / homeowner page — no login, no PIN */}
                            <Route path="/p/:handle" element={<PublicPage />} />
                            <Route path="/payment/success" element={<PaymentResult kind="success" />} />
                            <Route path="/payment/cancel" element={<PaymentResult kind="cancel" />} />
                            <Route path="*" element={<Gate />} />
                        </Routes>
                        </Suspense>
                    </BrowserRouter>
                </SiteConfigProvider>
                <Toaster
                    theme="dark"
                    position="top-center"
                    toastOptions={{
                        style: {
                            background: "rgba(11,11,14,0.92)",
                            border: "1px solid rgba(255,122,0,0.28)",
                            color: "#fff",
                            backdropFilter: "blur(12px)",
                        },
                    }}
                />
            </AuthProvider>
        </div>
    );
}

export default App;
